/**
 * Sweep the driver crank through a full turn and record the path of the coupler
 * point. Each sample is warm-started from the previous one so the solver stays
 * on a single assembly branch (the "circuit" of the linkage).
 */

import { Value } from "../autodiff/value.js";
import { Vec2 } from "../autodiff/vec2.js";
import { MechanismSpec, freeCount } from "./types.js";
import {
  PositionSolution,
  SolveOptions,
  solvePosition,
  differentiablePose,
} from "./solve.js";

export interface TraceOptions extends SolveOptions {
  /** Number of driver angles sampled over [0, 2*pi). */
  samples?: number;
  /** Start angle (radians). */
  startAngle?: number;
  /** Sweep extent (radians); defaults to a full turn. */
  sweep?: number;
}

export interface TraceResult {
  /** Driver angles actually used. */
  thetas: number[];
  /** Coupler-point path, one [x, y] per theta. */
  curve: Array<[number, number]>;
  /** Converged-solution flag per sample. */
  converged: boolean[];
  /** Full per-sample solutions (joint coordinates etc.). */
  solutions: PositionSolution[];
  /** Fraction of samples that converged. */
  convergedFraction: number;
  /** True if every sample converged. */
  ok: boolean;
}

/** Initial Newton seed for the first sample: the spec's free-joint guesses. */
function initialSeed(m: MechanismSpec): number[] {
  const seed: number[] = [];
  for (let i = 0; i < freeCount(m); i++) {
    seed.push(m.free[i].guess[0], m.free[i].guess[1]);
  }
  return seed;
}

export function traceCoupler(m: MechanismSpec, opts: TraceOptions = {}): TraceResult {
  const samples = opts.samples ?? 96;
  const startAngle = opts.startAngle ?? 0;
  const sweep = opts.sweep ?? 2 * Math.PI;

  const thetas: number[] = [];
  for (let i = 0; i < samples; i++) {
    thetas.push(startAngle + (sweep * i) / samples);
  }

  const curve: Array<[number, number]> = [];
  const converged: boolean[] = [];
  const solutions: PositionSolution[] = [];

  let seed = initialSeed(m);
  // A short pre-roll so the very first recorded sample is already on-branch.
  let preRoll = solvePosition(m, startAngle, seed, opts);
  for (let k = 0; k < 3 && preRoll.converged; k++) {
    preRoll = solvePosition(m, startAngle, preRoll.q, opts);
  }
  if (preRoll.converged) seed = preRoll.q;

  // Pass 1: warm-started Newton around the loop.
  for (const theta of thetas) {
    const sol = solvePosition(m, theta, seed, opts);
    solutions.push(sol);
    converged.push(sol.converged);
    curve.push(sol.coupler);
    if (sol.converged) seed = sol.q;
  }

  // Pass 2: reject samples where the coupler point teleports relative to the
  // previous accepted one — that is an assembly-mode (circuit) switch, not a
  // continuous motion. Threshold adapts to the median step of the trace.
  const steps: number[] = [];
  for (let i = 1; i < curve.length; i++) {
    if (converged[i] && converged[i - 1]) {
      steps.push(Math.hypot(curve[i][0] - curve[i - 1][0], curve[i][1] - curve[i - 1][1]));
    }
  }
  if (steps.length > 4) {
    const sorted = [...steps].sort((a, b) => a - b);
    const median = sorted[sorted.length >> 1] || 1e-9;
    const limit = Math.max(median * 8, median + 1e-9);
    let last: [number, number] | null = null;
    for (let i = 0; i < curve.length; i++) {
      if (!converged[i]) {
        last = null;
        continue;
      }
      if (last) {
        const jump = Math.hypot(curve[i][0] - last[0], curve[i][1] - last[1]);
        if (jump > limit) {
          converged[i] = false;
          last = null;
          continue;
        }
      }
      last = curve[i];
    }
  }

  const convergedCount = converged.filter(Boolean).length;
  return {
    thetas,
    curve,
    converged,
    solutions,
    convergedFraction: convergedCount / samples,
    ok: convergedCount === samples,
  };
}

/**
 * Differentiable coupler path: for each driver angle, take one autodiff Newton
 * step from the pre-computed converged seed `qStars[i]`. The returned Vec2s are
 * functions of the parameter Values `p`.
 */
export function differentiableCurve(
  base: MechanismSpec,
  p: Value[],
  thetas: number[],
  qStars: number[][],
): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < thetas.length; i++) {
    out.push(differentiablePose(base, p, thetas[i], qStars[i]).coupler);
  }
  return out;
}
